import {
  getStreamUploadEndpoint,
  isStreamPlayerHost,
  isStreamUid,
  readStreamPosterSeconds,
  readUidFromStreamPlayerSrc,
} from './streamVideo';
import { buildLoungeAuthHeaders } from './loungeAuthHeader';

export const STREAM_LIFECYCLE_RETRIES = 3;
export const STREAM_FINALIZE_TIMEOUT_MS = 8000;
export const STREAM_FINALIZE_BACKOFF_MS = [300, 800, 1600];

export function getStreamFinalizeEndpoint() {
  const endpoint = getStreamUploadEndpoint();
  return endpoint ? `${endpoint}/finalize` : '';
}

export function getStreamDiscardEndpoint() {
  const endpoint = getStreamUploadEndpoint();
  return endpoint ? `${endpoint}/discard` : '';
}

function decodeHtmlAttr(value: string) {
  return value.replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&#39;/g, "'");
}

// uid -> chosen poster second (null when the player has no poster param).
export function extractStreamPostersFromHtml(html: string): Map<string, number | null> {
  const posters = new Map<string, number | null>();
  const iframeRe = /<iframe\b[^>]*src=["']([^"']+)["'][^>]*>/gi;
  let match: RegExpExecArray | null;
  const source = String(html || '');
  while ((match = iframeRe.exec(source)) !== null) {
    const src = decodeHtmlAttr(match[1] || '');
    if (!isStreamPlayerHost(src)) continue;
    const uid = readUidFromStreamPlayerSrc(src);
    if (uid && !posters.has(uid)) posters.set(uid, readStreamPosterSeconds(src));
  }
  return posters;
}

export function extractStreamUidsFromHtml(html: string): string[] {
  const uids = new Set<string>();
  const iframeRe = /<iframe\b[^>]*src=["']([^"']+)["'][^>]*>/gi;
  let match: RegExpExecArray | null;
  const source = String(html || '');
  while ((match = iframeRe.exec(source)) !== null) {
    const src = match[1] || '';
    if (!isStreamPlayerHost(src)) continue;
    const uid = readUidFromStreamPlayerSrc(src);
    if (uid) uids.add(uid);
  }
  return Array.from(uids);
}

function readLoungeToken(explicit?: string | null) {
  if (explicit) return explicit;
  try {
    return localStorage.getItem('token');
  } catch {
    return null;
  }
}

function delay(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

async function postStreamLifecycle(
  action: 'finalize' | 'discard',
  uid: string,
  token: string,
  thumbnailTimeSec: number | null = null
): Promise<boolean> {
  const endpoint =
    action === 'finalize' ? getStreamFinalizeEndpoint() : getStreamDiscardEndpoint();
  if (!endpoint || !isStreamUid(uid) || !token) return false;
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), STREAM_FINALIZE_TIMEOUT_MS);
  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        ...buildLoungeAuthHeaders(token),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(
        action === 'finalize' && thumbnailTimeSec !== null ? { uid, thumbnailTimeSec } : { uid }
      ),
      keepalive: true,
      signal: controller.signal,
    });
    return response.ok;
  } finally {
    window.clearTimeout(timer);
  }
}

async function withLifecycleRetry(
  action: 'finalize' | 'discard',
  uid: string,
  token: string,
  thumbnailTimeSec: number | null = null
): Promise<boolean> {
  for (let attempt = 0; attempt < STREAM_LIFECYCLE_RETRIES; attempt += 1) {
    try {
      if (await postStreamLifecycle(action, uid, token, thumbnailTimeSec)) return true;
    } catch {
      // Keep retrying. Posted videos never expire; a failed finalize stays pending.
    }
    if (attempt < STREAM_LIFECYCLE_RETRIES - 1) {
      await delay(STREAM_FINALIZE_BACKOFF_MS[attempt] || 1600);
    }
  }
  return false;
}

export async function discardStreamVideo(
  uid: string,
  options: { token?: string | null } = {}
): Promise<boolean> {
  const token = readLoungeToken(options.token);
  if (!token || !isStreamUid(uid)) return false;
  return withLifecycleRetry('discard', uid, token);
}

export async function finalizePostedStreamVideos(
  html: string,
  options: { token?: string | null } = {}
): Promise<void> {
  const token = readLoungeToken(options.token);
  if (!token) return;
  const posters = extractStreamPostersFromHtml(html);
  await Promise.all(
    Array.from(posters.entries()).map(([uid, seconds]) =>
      withLifecycleRetry('finalize', uid, token, seconds)
    )
  );
}
